import { useAuth } from '@clerk/nextjs';
import { connectSingleton } from '@useparagon/connect';
import { hasSubscription } from '@web/guards/Subscription';
import { App } from '@web/query/App';
import { useParagonLoadState } from '@web/store/App/useParagonLoadState';
import React from 'react';

/**
 * The Paragon SDK restores its last session from localStorage the moment it is imported, on
 * every page, and fetches the project, user and integrations for it. Nothing ends that session,
 * so it outlives sign-out and org switches (the token lasts 10 days). Log it out whenever it can
 * no longer belong to the current user: signed out, a different org, or an org with no subscription.
 */
function SyncParagonSession() {
  const { isLoaded, isSignedIn, orgId } = useAuth();
  // Observe only: this mounts before ClerkTokenInjector, so a fetch from here would go out without a token.
  const { data: subscription, isSuccess } = App.useSubscriptions({ refetchIntervalInMilliSecond: false, enabled: false });
  const setLoadingState = useParagonLoadState((state) => state.setLoadingState);
  const lastOrgId = React.useRef<string | null>(null);

  const logout = React.useCallback(() => {
    if (!connectSingleton.getUser().authenticated) return;

    connectSingleton.logout();
    setLoadingState('unauthenticated');
  }, [setLoadingState]);

  React.useEffect(() => {
    if (isLoaded && !isSignedIn) logout();
  }, [isLoaded, isSignedIn, logout]);

  React.useEffect(() => {
    if (!orgId) return;
    if (lastOrgId.current && lastOrgId.current !== orgId) logout();
    lastOrgId.current = orgId;
  }, [orgId, logout]);

  React.useEffect(() => {
    if (isSuccess && !hasSubscription(subscription)) logout();
  }, [isSuccess, subscription, logout]);

  return null;
}

export { SyncParagonSession };
